// @ts-nocheck
/**
 * Product Card Gallery Manager (Centralized)
 * 
 * Handles media filtering and hover previews for ALL card types:
 * - Variant cards (collection page)
 * - Regular product cards (all pages)
 * - Recommendation cards
 * 
 * Features:
 * - Smooth hover previews (no click needed)
 * - Centralized media filtering by color (shows ALL media for selected color)
 * - Works for all card types using ProductCardServices
 * 
 * Version: 2025-01-28-centralized-services
 */

import { ProductCardServices } from './product-card-services.js';
import { productCardMediaFilter } from './product-card-media-filter.js';

/** Mobile / touch: skip heavy slideshow overrides; Liquid renders visible slides */
function isMobileViewport() {
  return window.matchMedia('(max-width: 749px)').matches;
}

/** Collection pages: init galleries immediately */
function shouldDeferGalleryInit() {
  return false;
}

function isCollectionPage() {
  return document.querySelector('.main-collection-grid') != null;
}

/** First visible row on collection — interactive immediately */
function isPriorityGallery(cardGallery) {
  const item = cardGallery.closest('[data-is-first-batch]');
  if (item?.getAttribute('data-is-first-batch') === 'true') return true;
  return false;
}

class VariantCardGallery {
  constructor() {
    this.initialized = new WeakSet();
    this.originalMethods = new WeakMap();
    this.revealObserver = null;
    this.slideCache = new WeakMap();
    this._initObserver = null;
    this._initQueue = [];
    this._initScheduled = false;
  }


  /**
   * Initialize all variant cards - 100% optimized with progressive loading
   * Also initializes regular product cards with color swatches (filtered products)
   * AND recommendation cards with color swatches
   */
  init() {
    const variantCardGalleries = document.querySelectorAll('[data-collection-variant-id] .card-gallery');
    
    const regularProductCards = document.querySelectorAll('product-card:not([data-collection-variant-id])');
    /** @type {Element[]} */
    const regularCardGalleries = [];
    regularProductCards.forEach((card) => {
      const gallery = card.querySelector('.card-gallery');
      if (gallery) regularCardGalleries.push(gallery);
    });

    const recommendationCards = document.querySelectorAll(
      '.resource-list__item product-card, ' +
      '.resource-list__item .product-card, ' +
      'product-recommendations product-card, ' +
      '.product-recommendations product-card, ' +
      '.recently-viewed-products product-card, ' +
      '[data-recently-viewed-grid] .resource-list__item'
    );

    /** @type {Element[]} */
    const recommendationCardGalleries = [];
    const uniqueCards = new Set(Array.from(recommendationCards));
    uniqueCards.forEach((card) => {
      const gallery = card.querySelector?.('.card-gallery') || (card.classList?.contains('card-gallery') ? card : null);
      if (gallery instanceof Element) recommendationCardGalleries.push(gallery);
    });

    const standaloneGalleries = document.querySelectorAll(
      '.resource-list__item .card-gallery, [data-recently-viewed-grid] .card-gallery, recently-viewed-products .card-gallery'
    );
    
    const allGalleries = Array.from(new Set([
      ...variantCardGalleries,
      ...regularCardGalleries,
      ...recommendationCardGalleries,
      ...standaloneGalleries,
    ]));
    if (allGalleries.length === 0) {
      return;
    }

    const onCollection = isCollectionPage();
    const rootMargin = onCollection ? '120px' : '120px';

    if (typeof IntersectionObserver !== 'undefined') {
      if (this._initObserver) {
        this._initObserver.disconnect();
      }

      this._initObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.#enqueueInit(/** @type {Element} */ (entry.target));
            this._initObserver?.unobserve(entry.target);
          }
        });
      }, {
        rootMargin,
        threshold: 0.01
      });
      
      allGalleries.forEach((gallery) => {
        if (onCollection && isPriorityGallery(gallery)) {
          this.initCard(gallery);
          return;
        }
        this._initObserver.observe(gallery);
      });
    } else {
      const priority = onCollection ? allGalleries.filter(isPriorityGallery) : allGalleries.slice(0, 6);
      priority.forEach((gallery) => this.initCard(gallery));
      allGalleries
        .filter((gallery) => !priority.includes(gallery))
        .forEach((gallery) => this.#enqueueInit(gallery));
    }
  }

  /** Public wrapper for queued init (used by mutation observer) */
  queueInit(gallery) {
    this.#enqueueInit(gallery);
  }

  /** Queue card init — one card per idle frame to avoid main-thread blocking */
  #enqueueInit(gallery) {
    if (this.initialized.has(gallery)) return;
    this._initQueue.push(gallery);
    if (!this._initScheduled) {
      this._initScheduled = true;
      this.#flushInitQueue();
    }
  }

  #flushInitQueue() {
    const batchSize = isCollectionPage() ? 2 : 3;

    const runBatch = () => {
      for (let i = 0; i < batchSize && this._initQueue.length > 0; i += 1) {
        const gallery = this._initQueue.shift();
        if (gallery) this.initCard(gallery);
      }

      if (this._initQueue.length === 0) {
        this._initScheduled = false;
        return;
      }

      if (window.requestIdleCallback) {
        requestIdleCallback(runBatch, { timeout: 800 });
      } else {
        requestAnimationFrame(runBatch);
      }
    };

    runBatch();
  }

  /**
   * Initialize a single variant card (100% optimized with error handling)
   * @param {Element} cardGallery
   */
  initCard(cardGallery) {
    // Debug removed - see Liquid debug logs instead

    if (this.initialized.has(cardGallery)) {
      return;
    }
    this.initialized.add(cardGallery);

    try {
      const productCard =
        cardGallery.closest('product-card') ||
        cardGallery.closest('.resource-list__item') ||
        cardGallery.parentElement;

      if (!productCard) {
        return;
      }

      const card = cardGallery.closest('[data-collection-variant-id]');
      const slideshow = cardGallery.querySelector('slideshow-component');
      
      if (!slideshow) {
        let retries = 0;
        const maxRetries = 5;
        const retry = () => {
          if (retries < maxRetries) {
            retries++;
            setTimeout(() => {
              const retrySlideshow = cardGallery.querySelector('slideshow-component');
              if (retrySlideshow) {
                this.continueInit(cardGallery, productCard, retrySlideshow);
              } else {
                retry();
              }
            }, 100 * retries);
          }
        };
        retry();
        return;
      }

      this.continueInit(cardGallery, productCard, slideshow);
    } catch {
      /* Skip failed card init without marking gallery as broken */
    }
  }

  /**
   * Navigable slides for carousel (respects swipe limit).
   * @param {Element} cardGallery
   * @param {any} [slideshow]
   */
  getNavigableSlidesForGallery(cardGallery, slideshow = null) {
    const gallery =
      cardGallery instanceof HTMLElement ? cardGallery : slideshow?.closest?.('.card-gallery');
    if (!(gallery instanceof HTMLElement)) return [];

    const fromRefs = slideshow?.refs?.slides;
    const slides = fromRefs
      ? Array.from(fromRefs)
      : Array.from(gallery.querySelectorAll('slideshow-slide'));
    return ProductCardServices.getNavigableSlides(gallery, slides);
  }

  /**
   * @deprecated Use getNavigableSlidesForGallery
   * @param {any} slideshow
   */
  getVisibleSlides(slideshow) {
    const gallery = slideshow?.closest?.('.card-gallery');
    if (gallery) return this.getNavigableSlidesForGallery(gallery, slideshow);
    if (!slideshow) return [];
    const slides = slideshow.refs?.slides ? Array.from(slideshow.refs.slides) : [];
    return slides.filter((slide) => ProductCardServices.isSlideVisible(slide));
  }
  
  /**
   * Helper to get all slides (including hidden)
   * @param {any} slideshow
   * @returns {Element[]}
   */
  getAllSlides(slideshow) {
    if (!slideshow || !slideshow.refs || !slideshow.refs.slides) return [];
    return Array.from(slideshow.refs.slides);
  }
  
  /**
   * Helper to get next visible slide index
   * @param {any} slideshow
   * @param {number} currentIndex
   * @returns {number}
   */
  getNextVisibleIndex(slideshow, currentIndex) {
    const visibleSlides = this.getVisibleSlides(slideshow);
    if (visibleSlides.length === 0) return currentIndex;
    
    const allSlides = this.getAllSlides(slideshow);
    if (allSlides.length === 0) return currentIndex;
    
    const currentSlide = allSlides[currentIndex];
    if (!currentSlide) {
      // Current index invalid, return first visible
      return visibleSlides[0] ? allSlides.indexOf(visibleSlides[0]) : currentIndex;
    }
    
    // Find current slide's position in visible slides
    const currentVisibleIndex = visibleSlides.indexOf(currentSlide);
    if (currentVisibleIndex === -1) {
      // Current slide is hidden, find first visible
      return visibleSlides[0] ? allSlides.indexOf(visibleSlides[0]) : currentIndex;
    }
    
    // Get next visible slide (with infinite loop)
    const nextVisibleIndex = (currentVisibleIndex + 1) % visibleSlides.length;
    const nextVisibleSlide = visibleSlides[nextVisibleIndex];
    return nextVisibleSlide ? allSlides.indexOf(nextVisibleSlide) : currentIndex;
  }
  
  /**
   * Helper to get previous visible slide index
   * @param {any} slideshow
   * @param {number} currentIndex
   * @returns {number}
   */
  getPreviousVisibleIndex(slideshow, currentIndex) {
    const visibleSlides = this.getVisibleSlides(slideshow);
    if (visibleSlides.length === 0) return currentIndex;
    
    const allSlides = this.getAllSlides(slideshow);
    if (allSlides.length === 0) return currentIndex;
    
    const currentSlide = allSlides[currentIndex];
    if (!currentSlide) {
      // Current index invalid, return last visible
      const lastVisible = visibleSlides[visibleSlides.length - 1];
      return lastVisible ? allSlides.indexOf(lastVisible) : currentIndex;
    }
    
    // Find current slide's position in visible slides
    const currentVisibleIndex = visibleSlides.indexOf(currentSlide);
    if (currentVisibleIndex === -1) {
      // Current slide is hidden, find last visible
      const lastVisible = visibleSlides[visibleSlides.length - 1];
      return lastVisible ? allSlides.indexOf(lastVisible) : currentIndex;
    }
    
    // Get previous visible slide (with infinite loop)
    const prevVisibleIndex = currentVisibleIndex === 0 
      ? visibleSlides.length - 1 
      : currentVisibleIndex - 1;
    const prevVisibleSlide = visibleSlides[prevVisibleIndex];
    return prevVisibleSlide ? allSlides.indexOf(prevVisibleSlide) : currentIndex;
  }

  /**
   * On mobile, ensure visible slides have reveal so native horizontal scroll can swipe between them.
   * @param {Element} cardGallery
   * @param {any} slideshow
   */
  #ensureMobileSwipeSlides(cardGallery, slideshow) {
    if (!ProductCardServices.isTouchGalleryNav(cardGallery)) return;
    if (!slideshow?.refs?.slides) return;

    ProductCardServices.clearHoverPreview(cardGallery);
    ProductCardServices.enforceSwipeLimit(cardGallery);

    if (slideshow instanceof HTMLElement) {
      slideshow.removeAttribute('disabled');
    }

    this.#markGalleryReady(cardGallery);
  }

  /**
   * Re-enable arrows / touch swipe after media filter or init.
   * @param {Element} cardGallery
   * @param {any} slideshow
   * @param {Element | null} card
   * @param {object} [options]
   * @param {() => void} [options.bindSlideshowOverrides]
   * @param {boolean} [options.skipDesktopSlideshowOverrides]
   * @param {number} [options.visibleCount]
   */
  #refreshGalleryNavigation(cardGallery, slideshow, card, options = {}) {
    if (!slideshow) return;

    ProductCardServices.syncGalleryNavMode(cardGallery);

    const navigableCount = ProductCardServices.enforceSwipeLimit(cardGallery);
    const visibleSlides = ProductCardServices.getNavigableSlides(cardGallery);

    const arrows = cardGallery.querySelector('slideshow-arrows');
    if (arrows instanceof HTMLElement) {
      arrows.hidden = navigableCount <= 1;
      arrows.style.display = navigableCount <= 1 ? 'none' : '';
    }

    if (navigableCount <= 1) {
      slideshow.setAttribute('disabled', 'true');
      productCardMediaFilter.syncCollectionPagination(cardGallery, slideshow);
      if (visibleSlides.length > 0) {
        ProductCardServices.paintCardGallerySlide(cardGallery, slideshow, visibleSlides[0], visibleSlides);
      }
      return;
    }

    slideshow.removeAttribute('disabled');

    if (!options.skipDesktopSlideshowOverrides && options.bindSlideshowOverrides) {
      options.bindSlideshowOverrides();
    }

    if (ProductCardServices.isTouchGalleryNav(cardGallery)) {
      this.#ensureMobileSwipeSlides(cardGallery, slideshow);
    }

    productCardMediaFilter.syncCollectionPagination(cardGallery, slideshow);

    if (visibleSlides.length > 0) {
      const first = visibleSlides[0];
      ProductCardServices.paintCardGallerySlide(cardGallery, slideshow, first, visibleSlides);
    }
  }
  
  /**
   * Continue initialization after slideshow is ready
   * @param {Element} cardGallery
   * @param {Element} productCard
   * @param {any} slideshow
   */
  continueInit(cardGallery, productCard, slideshow) {
    try {
      ProductCardServices.syncGalleryNavMode(cardGallery);
      ProductCardServices.enforceSwipeLimit(cardGallery);

      const card = cardGallery.closest('[data-collection-variant-id]');
      const skipDesktopSlideshowOverrides = card && isMobileViewport();
      
      cardGallery.addEventListener(
        'card-gallery:filtered',
        (event) => {
          this.slideCache.delete(cardGallery);
          const visibleCount = event.detail?.count || 0;
          this.#refreshGalleryNavigation(cardGallery, slideshow, card, {
            bindSlideshowOverrides,
            skipDesktopSlideshowOverrides,
            visibleCount,
          });
        },
        { passive: true }
      );

      const bindSlideshowOverrides = () => {
        if (!this.slideCache.has(cardGallery)) {
          const allSlides = Array.from(cardGallery.querySelectorAll('slideshow-slide'));
          this.slideCache.set(cardGallery, allSlides);
        }

        if (!slideshow._originalNext) {
          slideshow._originalNext = slideshow.next.bind(slideshow);
          slideshow._originalPrevious = slideshow.previous.bind(slideshow);
          slideshow._originalSelect = slideshow.select.bind(slideshow);
        }

        slideshow.next = function (event, options) {
          event?.preventDefault?.();
          ProductCardServices.navigateCarousel(cardGallery, this, 'next');
        }.bind(slideshow);

        slideshow.previous = function (event, options) {
          event?.preventDefault?.();
          ProductCardServices.navigateCarousel(cardGallery, this, 'previous');
        }.bind(slideshow);

        slideshow.select = function (input, event, options = {}) {
          const isHoverPreview = options.hoverPreview === true;

          if (!isHoverPreview) {
            ProductCardServices.clearHoverPreview(cardGallery);
          }

          let resolved = input;
          let targetSlide = null;
          if (typeof input === 'number' && !isHoverPreview) {
            const navigable = ProductCardServices.getNavigableSlides(cardGallery);
            const target = navigable[input];
            if (target instanceof HTMLElement) {
              targetSlide = target;
              const slideId = target.getAttribute('slide-id') || target.getAttribute('data-media-id');
              if (slideId) resolved = { id: slideId };
            }
          } else if (input && typeof input === 'object' && 'id' in input) {
            targetSlide =
              cardGallery.querySelector(`slideshow-slide[slide-id="${CSS.escape(String(input.id))}"]`) ||
              cardGallery.querySelector(`slideshow-slide[data-media-id="${CSS.escape(String(input.id))}"]`);
          }

          const result = this._originalSelect(resolved, event, options);

          // Base select() strips reveal / aria-hidden — restore so filtered slides stay visible.
          if (!isHoverPreview && targetSlide instanceof HTMLElement) {
            const navigable = ProductCardServices.getNavigableSlides(cardGallery);
            ProductCardServices.paintCardGallerySlide(cardGallery, this, targetSlide, navigable);
            ProductCardServices.syncSwatchToSlideColor(cardGallery, targetSlide);
            productCardMediaFilter.syncCollectionPagination(cardGallery, this);
          } else if (!isHoverPreview) {
            productCardMediaFilter.syncCollectionPagination(cardGallery, this);
          }
          return result;
        }.bind(slideshow);
      };

      if (slideshow && !skipDesktopSlideshowOverrides) {
        bindSlideshowOverrides();
      }

      this.#refreshGalleryNavigation(cardGallery, slideshow, card, {
        bindSlideshowOverrides,
        skipDesktopSlideshowOverrides,
      });
      
      // Use arrow function to preserve 'this' context for private method access
      const waitForVariantPicker = () => {
        const variantPicker = productCard.querySelector('swatches-variant-picker-component');
        
        // For variant cards, variant picker is optional (may not exist)
        if (!variantPicker && !card) {
          let retries = 0;
          const maxRetries = 10;

          const finishInit = (picker) => {
            this.setupMediaFiltering(productCard, slideshow, picker, cardGallery);
            const hasActiveColor = Boolean(cardGallery.dataset.activeFilterColor);
            if (!hasActiveColor && !skipDesktopSlideshowOverrides) {
              this.ensureFirstSlideSelected(slideshow, cardGallery);
            } else if (!hasActiveColor) {
              this.#markGalleryReady(cardGallery);
            } else {
              this.#markGalleryReady(cardGallery);
            }
            if (isMobileViewport() || window.matchMedia('(hover: none)').matches || window.matchMedia('(pointer: coarse)').matches) {
              this.#ensureMobileSwipeSlides(cardGallery, slideshow);
            }
            this.#refreshGalleryNavigation(cardGallery, slideshow, card, {
              bindSlideshowOverrides,
              skipDesktopSlideshowOverrides,
            });
          };

          const retry = () => {
            if (retries >= maxRetries) {
              finishInit(null);
              return;
            }

            retries++;
            setTimeout(() => {
              const picker = productCard.querySelector('swatches-variant-picker-component');
              if (picker) {
                finishInit(picker);
              } else {
                retry();
              }
            }, 50);
          };

          retry();
          return;
        }

        // Setup media filtering and hover previews
        // Use 'this' directly in arrow function - it's preserved from outer scope
        this.setupMediaFiltering(productCard, slideshow, variantPicker, cardGallery);
        
        const hasActiveColor = Boolean(cardGallery.dataset.activeFilterColor);
        if (!hasActiveColor) {
          if (!skipDesktopSlideshowOverrides) {
            this.ensureFirstSlideSelected(slideshow, cardGallery);
          } else {
            this.#markGalleryReady(cardGallery);
          }
        } else {
          this.#markGalleryReady(cardGallery);
        }
        if (isMobileViewport() || window.matchMedia('(hover: none)').matches || window.matchMedia('(pointer: coarse)').matches) {
          this.#ensureMobileSwipeSlides(cardGallery, slideshow);
        }
        this.#refreshGalleryNavigation(cardGallery, slideshow, card, {
          bindSlideshowOverrides,
          skipDesktopSlideshowOverrides,
        });
      };

      waitForVariantPicker();
    } catch {
      /* Skip without data-error overlay */
    }
  }

  /**
   * @param {Element} cardGallery
   */
  #markGalleryReady(cardGallery) {
    cardGallery.removeAttribute('data-error');
    cardGallery.classList.add('gallery-initialized');
  }

  /**
   * Setup media filtering - For variant cards, filter by color and allow color changes via swatch
   * @param {Element} productCard
   * @param {any} slideshow
   * @param {any} variantPicker
   * @param {Element} cardGallery
   */
  setupMediaFiltering(productCard, slideshow, variantPicker, cardGallery) {
    productCardMediaFilter.init(cardGallery, productCard, slideshow, variantPicker);
  }

  /**
   * Ensure first visible slide is selected - 100% reliable helper
   * @param {any} slideshow
   * @param {Element} cardGallery
   */
  ensureFirstSlideSelected(slideshow, cardGallery) {
    requestAnimationFrame(() => {
      if (!(cardGallery instanceof HTMLElement) || !slideshow) {
        return;
      }

      const activeColor =
        cardGallery.dataset.activeFilterColor ||
        ProductCardServices.getColorValue(cardGallery) ||
        ProductCardServices.getColorValue(cardGallery.closest('product-card'));

      if (activeColor) {
        ProductCardServices.applyGalleryColorToCard(cardGallery, slideshow, activeColor);
        this.#markGalleryReady(cardGallery);
        return;
      }

      const navigable = ProductCardServices.getNavigableSlides(cardGallery);
      const slideToShow = navigable[0];
      if (!slideToShow) {
        this.#markGalleryReady(cardGallery);
        return;
      }

      const slideId = slideToShow.getAttribute('slide-id') || slideToShow.getAttribute('data-media-id');
      const selectFn = slideshow._originalSelect?.bind(slideshow);
      if (selectFn && slideId) {
        selectFn({ id: slideId }, undefined, { animate: false });
      }
      ProductCardServices.enforceSwipeLimit(cardGallery);
      this.#markGalleryReady(cardGallery);
    });
  }

  /**
   * Update media for selected color (only for regular product cards, not variant cards)
   */
  updateMediaForSelectedColor(productCard, slideshow, variantPicker) {
    const card = productCard.closest('[data-collection-variant-id]');
    if (card) return;

    const selectedOption = variantPicker.selectedOption;
    if (!selectedOption) return;

    let selectedColor = '';
    if (selectedOption instanceof HTMLInputElement) {
      selectedColor = selectedOption.value || '';
    } else if (selectedOption.value) {
      selectedColor = selectedOption.value;
    }

    if (!selectedColor) return;

    const cardGallery = productCard.querySelector('.card-gallery');
    if (!cardGallery) return;

    productCardMediaFilter.filterMediaByColor(cardGallery, slideshow, selectedColor, 'regular');
  }

  /**
   * @param {any} slideshow
   * @param {string} colorValue
   */
  filterMediaDirectly(slideshow, colorValue) {
    if (!colorValue || !slideshow) return;
    const cardGallery = slideshow.closest('.card-gallery');
    if (!cardGallery) return;
    productCardMediaFilter.filterMediaByColor(cardGallery, slideshow, colorValue, 'regular');
  }
}

// Initialize
const variantCardGallery = new VariantCardGallery();

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => variantCardGallery.init());
    });
  });
} else {
  requestAnimationFrame(() => variantCardGallery.init());
}

// Watch for dynamically added cards (variant cards, regular product cards, and recommendation cards with swatches)
// Use shared observer pattern to reduce overhead - both files can use the same observer
if (window.MutationObserver) {
  // Initialize shared observer manager if it doesn't exist
  if (!window.__variantCardsObserverManager) {
    window.__variantCardsObserverManager = {
      observer: null,
      handlers: [],
      setup() {
        if (this.observer) return;
        
        this.observer = new MutationObserver((mutations) => {
          // Call all registered handlers
          this.handlers.forEach(handler => {
            try {
              handler(mutations);
            } catch (e) {
              // Silent error handling
            }
          });
        });
        
        const observeRoot = () => {
          const root =
            document.getElementById('ResultsList') ||
            document.getElementById('MainContent') ||
            document.body;

          if (root) {
            this.observer.observe(root, {
              childList: true,
              subtree: true
            });
          } else {
            setTimeout(observeRoot, 100);
          }
        };
        
        observeRoot();
      },
      register(handler) {
        this.handlers.push(handler);
        if (!this.observer) {
          this.setup();
        }
      }
    };
  }
  
  // Register this module's handler
  window.__variantCardsObserverManager.register((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType !== 1 || !(node instanceof Element)) return;

        const galleries = node.matches('.card-gallery')
          ? [node]
          : Array.from(node.querySelectorAll('.card-gallery'));

        galleries.forEach((gallery) => {
          variantCardGallery.queueInit(gallery);
        });
      });
    });
  });
}

// Test if module is executing
export default variantCardGallery;




